import { Component, OnInit, ViewChild } from '@angular/core';
import { PoBreadcrumb, PoNotificationService, PoPageFilter, PoPageListComponent } from '@po-ui/ng-components';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-processos',
  templateUrl: './processos.component.html',
  styleUrls: ['./processos.component.css'],
  standalone: false
})
export class ProcessosComponent implements OnInit {

  @ViewChild('pageListComponent', { static: true }) pageListComponent!: PoPageListComponent;

  numeroProcesso: string = '';
  loading: boolean = false;
  processoData: any = null;
  movimentacoesAgrupadas: Array<{ mesAno: string; movimentacoes: Array<{ dia: string; content: string }> }> = [];
  mensagemAguarde: string = '';
  
  disclaimerGroup: any;
  private disclaimers: any[] = [];

  public readonly filterSettings: PoPageFilter = {
    action: this.filterAction.bind(this),
    placeholder: 'Digite o número do processo',
    width: 6
  };

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Processos' }
    ]
  };

  constructor(
    private readonly http: HttpClient,
    private readonly poNotificationService: PoNotificationService
  ) { }

  ngOnInit(): void {
    this.disclaimerGroup = {
      title: 'Filtros:',
      disclaimers: [],
      change: this.onChangeDisclaimer.bind(this),
      remove: this.onClearDisclaimer.bind(this)
    };
  }

  populateDisclaimers(mensagem: string, isAguarde: boolean = false) {
    if (mensagem && mensagem.trim() !== '') {
      this.disclaimers = [{ value: mensagem, property: isAguarde ? 'info' : 'search' }];
      this.disclaimerGroup.disclaimers = [...this.disclaimers];
    } else {
      this.disclaimers = [];
      this.disclaimerGroup.disclaimers = [];
    }
  }

  filterAction(labelFilter: string | Array<string>) {
    const filter = typeof labelFilter === 'string' ? labelFilter : labelFilter[0] || '';
    this.numeroProcesso = filter;
    this.mensagemAguarde = 'Aguarde: estamos buscando as informações solicitadas...';
    this.buscarProcesso();
  }

  onChangeDisclaimer(disclaimers: any) {
    this.disclaimers = disclaimers;
  }

  onClearDisclaimer(disclaimers: any) {
    if (disclaimers.removedDisclaimer && (disclaimers.removedDisclaimer.property === 'search' || disclaimers.removedDisclaimer.property === 'info')) {
      this.pageListComponent.clearInputSearch();
    }
    this.disclaimers = [];
    this.numeroProcesso = '';
    this.mensagemAguarde = '';
    this.processoData = null;
    this.movimentacoesAgrupadas = [];
  }

  buscarProcesso(): void {
    const numeroSomenteDigitos = (this.numeroProcesso || '').replace(/\D/g, '');

    if (numeroSomenteDigitos.length !== 20) {
      this.mensagemAguarde = ''; // Limpa a mensagem de aguarde quando a validação falha
      this.poNotificationService.warning('O campo não corresponde a um número de Processo válido.');
      return;
    }

    const numeroFormatado =
      `${numeroSomenteDigitos.substring(0, 7)}-` +
      `${numeroSomenteDigitos.substring(7, 9)}.` +
      `${numeroSomenteDigitos.substring(9, 13)}.` +
      `${numeroSomenteDigitos.substring(13, 14)}.` +
      `${numeroSomenteDigitos.substring(14, 16)}.` +
      `${numeroSomenteDigitos.substring(16, 20)}`;

    this.numeroProcesso = numeroFormatado;

    this.loading = true;
    this.processoData = null;

    const apiUrl = `${environment.apiUrl}${environment.versionApi}/processos/consulta-processo-formatado/${this.numeroProcesso}`;

    this.http.get(apiUrl).subscribe({
      next: (data) => {
        this.processoData = data;
        this.processarMovimentacoes(data);
        this.loading = false;
        this.mensagemAguarde = ''; // Limpa a mensagem após buscar
        this.poNotificationService.success('Processo encontrado com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao buscar processo:', error);
        this.loading = false;
        this.mensagemAguarde = ''; // Limpa a mensagem em caso de erro
        if (error.status === 404) {
          this.poNotificationService.warning('Processo não encontrado.');
        } else {
          this.poNotificationService.error('Erro ao buscar processo. Por favor, tente novamente.');
        }
      }
    });
  }


  limpar(): void {
    this.numeroProcesso = '';
    this.processoData = null;
    this.movimentacoesAgrupadas = [];
  }

  processarMovimentacoes(data: any): void {
    if (!data || !data.steps || !Array.isArray(data.steps)) {
      this.movimentacoesAgrupadas = [];
      return;
    }

    // Agrupar por mês/ano
    const agrupadas: Map<string, Array<{ dia: string; content: string }>> = new Map();

    data.steps.forEach((step: any) => {
      if (step.step_date && step.content) {
        const date = new Date(step.step_date);
        const mesAno = this.formatarMesAno(date);
        const dia = date.getDate().toString();

        if (!agrupadas.has(mesAno)) {
          agrupadas.set(mesAno, []);
        }

        agrupadas.get(mesAno)!.push({
          dia: dia,
          content: step.content
        });
      }
    });

    // Converter para array e ordenar por data (mais recente primeiro)
    this.movimentacoesAgrupadas = Array.from(agrupadas.entries())
      .map(([mesAno, movimentacoes]) => ({
        mesAno: mesAno,
        movimentacoes: movimentacoes.sort((a, b) => parseInt(b.dia) - parseInt(a.dia))
      }))
      .sort((a, b) => {
        // Ordenar por data (mais recente primeiro)
        const dateA = this.parseMesAno(a.mesAno);
        const dateB = this.parseMesAno(b.mesAno);
        return dateB.getTime() - dateA.getTime();
      });
  }

  formatarMesAno(date: Date): string {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return `${meses[date.getMonth()]}/${date.getFullYear()}`;
  }

  parseMesAno(mesAno: string): Date {
    const [mes, ano] = mesAno.split('/');
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const mesIndex = meses.indexOf(mes);
    return new Date(parseInt(ano), mesIndex, 1);
  }

  getResumo(): string {
    return this.processoData?.ai_resume || '';
  }
}


